import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Phone, MapPin, Check } from "lucide-react";
import { localServices } from "@/data/local-services";
import { locationBySlug } from "@/data/locations";

type RegionData = {
  slug: string;
  name: string;
  bundesland: string;
  headline: string;
  intro: string;
  intro2: string;
  cities: string[]; // location slugs
};

const regions: Record<string, RegionData> = {
  industrieviertel: {
    slug: "industrieviertel",
    name: "Industrieviertel",
    bundesland: "Niederösterreich",
    headline: "Umzug, Transport & Entrümpelung im Industrieviertel",
    intro:
      "Das Industrieviertel im Süden Niederösterreichs ist unser wichtigstes Einsatzgebiet außerhalb von Wien. Von Wiener Neustadt über Baden, Mödling und Neunkirchen bis Bad Vöslau, Traiskirchen und Ebreichsdorf – wir kennen die Region, die Hausverwaltungen, die kommunalen Halteverbots-Verfahren und die Wege vor Ort. Sie erreichen unser Team meist innerhalb von 48 Stunden vor Ort, mit fixem Termin und schriftlichem Angebot.",
    intro2:
      "Wir arbeiten im gesamten Industrieviertel mit eigenem Team und eigenem Fuhrpark – ohne Subunternehmer. Für Umzüge planen wir Halteverbotszonen mit den jeweiligen Stadtgemeinden, für Entrümpelungen und Wohnungsauflösungen kalkulieren wir fair und übernehmen den Abtransport des Räumguts. Ihr Auftrag hat einen einzigen Ansprechpartner – von der Besichtigung bis zur besenreinen Schlüsselübergabe.",
    cities: [
      "wiener-neustadt",
      "baden",
      "moedling",
      "neunkirchen",
      "bad-voeslau",
      "traiskirchen",
      "ebreichsdorf",
      "brunn-am-gebirge",
      "perchtoldsdorf",
      "ternitz",
      "schwechat",
    ],
  },
  nordburgenland: {
    slug: "nordburgenland",
    name: "Nordburgenland",
    bundesland: "Burgenland",
    headline: "Umzug, Transport & Entrümpelung im Nordburgenland",
    intro:
      "Das Nordburgenland – rund um Eisenstadt, Mattersburg, Neusiedl am See und Rust – ist für unsere Teams von Wien und Wiener Neustadt aus in unter einer Stunde erreichbar. Wir übersiedeln Privat- und Firmenkunden, entrümpeln Wohnungen und Häuser und übernehmen Wohnungsauflösungen zum Angebot. Angebot in 24 Stunden, Termin oft binnen 48 Stunden.",
    intro2:
      "Wir kennen die burgenländische Bauweise – von den Winzerhäusern in Kleinhöflein und Rust bis zu den Genossenschaftsbauten in Eisenstadt und Mattersburg. Halteverbote koordinieren wir mit dem Bürgerservice der jeweiligen Stadtgemeinde. Für die weitere Behandlung des Räumguts arbeiten wir ausschließlich mit befugten burgenländischen Partnerbetrieben. Ergebnis: transparente Angebot, saubere Abwicklung und eine korrekte Übergabe an Ihre Hausverwaltung.",
    cities: [
      "eisenstadt",
      "mattersburg",
      "neusiedl-am-see",
      "rust",
      "oberpullendorf",
      "eisenstadt",
    ],
  },
};

export const Route = createFileRoute("/regionen/$region")({
  loader: ({ params }) => {
    const region = regions[params.region];
    if (!region) throw notFound();
    return { region };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const r = loaderData.region;
    const url = `https://schober-transportservice.at/regionen/${params.region}`;
    return {
      meta: [
        { title: `${r.headline} | Schober Transportservice` },
        {
          name: "description",
          content: `Umzug, Transport, Entrümpelung & Räumung im ${r.name} (${r.bundesland}) – Angebot, versichert bis 25.000 €, Termin in 48 h. Schober Transportservice ist Ihr regionaler Partner. ☎ +43 680 230 87 00.`,
        },
        { property: "og:title", content: `${r.headline} | Schober` },
        { property: "og:description", content: `Regionaler Partner für Umzug & Entrümpelung im ${r.name}.` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: RegionPage,
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Region nicht gefunden</h1>
      <p className="mt-4">
        <Link to="/leistungen" className="text-brand-orange underline">
          Zurück zu unseren Leistungen
        </Link>
      </p>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Fehler</h1>
      <p className="mt-4 text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function RegionPage() {
  const { region } = Route.useLoaderData();
  const uniqueCities = Array.from(new Set<string>(region.cities))
    .map((s) => locationBySlug[s])
    .filter(Boolean);

  return (
    <>
      <section className="relative bg-brand-navy py-20 text-white md:py-28">
        <div className="container mx-auto max-w-6xl px-4">
          <nav aria-label="Breadcrumb" className="mb-4 text-sm text-white/80">
            <Link to="/" className="hover:text-brand-orange-on-dark">Home</Link>
            <span className="mx-2">/</span>
            <span>Regionen</span>
            <span className="mx-2">/</span>
            <span className="text-white">{region.name}</span>
          </nav>
          <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight md:text-6xl">
            {region.headline}
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/90 md:text-xl">
            Ihr regionaler Partner für Umzug, Transport, Entrümpelung, Wohnungsauflösung, Firmen- und Büroumzug im {region.name} ({region.bundesland}).
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="tel:00436802308700"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 font-bold text-brand-orange-foreground transition hover:scale-105"
            >
              <Phone className="h-4 w-4" /> +43 680 230 87 00
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white px-6 py-3 font-bold text-white transition hover:bg-white hover:text-brand-navy"
            >
              Kostenloses Angebot <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="container mx-auto max-w-6xl px-4 py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-3">
          <article className="space-y-6 md:col-span-2">
            <p className="text-lg leading-relaxed text-foreground/90">{region.intro}</p>
            <p className="text-base leading-relaxed text-foreground/80">{region.intro2}</p>
          </article>
          <aside className="h-fit rounded-lg bg-muted p-6">
            <h2 className="font-display text-xl font-bold text-brand-navy">
              <MapPin className="inline h-5 w-5 text-brand-orange" /> Auf einen Blick
            </h2>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-brand-orange" /> Eigenes Team, kein Subunternehmer</li>
              <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-brand-orange" /> Versichert bis 25.000 €</li>
              <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-brand-orange" /> Termin oft binnen 48 h</li>
              <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-brand-orange" /> verbindliches Angebot</li>
              <li className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-brand-orange" /> Faire Preisgestaltung bei gut erhaltenem Inventar</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="bg-muted py-16">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
            Städte und Gemeinden im {region.name}
          </h2>
          <p className="mt-3 max-w-3xl text-foreground/80">
            Wählen Sie Ihre Stadt für alle Leistungen und lokale Details:
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {uniqueCities.map((c) => (
              <div key={c.slug} className="rounded-lg bg-background p-6 shadow-sm">
                <h3 className="font-display text-xl font-bold text-brand-navy">{c.name}</h3>
                {c.plz && <p className="text-sm text-muted-foreground">PLZ {c.plz}</p>}
                <ul className="mt-4 space-y-1.5 text-sm">
                  {localServices.map((s) => (
                    <li key={s.slug}>
                      <Link
                        to="/leistungen/$service/$ort"
                        params={{ service: s.slug, ort: c.slug }}
                        className="text-brand-navy transition hover:text-brand-orange"
                      >
                        › {s.title} {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-orange py-16">
        <div className="container mx-auto max-w-4xl px-4 text-center text-brand-orange-foreground">
          <h2 className="font-display text-3xl font-extrabold md:text-4xl">
            Ihr Auftrag im {region.name} – jetzt anfragen
          </h2>
          <p className="mt-4 text-lg">
            kostenlose Beratung innerhalb von 24 Stunden. Wir melden uns persönlich zurück.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a href="tel:00436802308700" className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-8 py-3.5 font-bold text-white transition hover:scale-105">
              <Phone className="h-4 w-4" /> Jetzt anrufen
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 font-bold text-brand-navy transition hover:scale-105">
              Online anfragen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
